import LeaderboardApi from '@/api/leaderboard'
import { isFeatureEnabled, getVariant } from './feature-toggles.module'

const _state = {
  isLeaderboardVisible: false,
}

const MUTATION_TYPES = {
  TOGGLE_LEADERBOARD_VISIBILITY: 'TOGGLE_LEADERBOARD_VISIBILITY',
}

const UNLEASH_FLAG_NEW_LEADERBOARD = 'AHA-42758-new-leaderboard'

const _getters = {
  quizSlideIdsUpTo: (_, __, rootState, rootGetters) => (order) => (rootState.slides || [])
    .filter((slide) => rootGetters.isQuizSlide(slide) && (order === undefined || slide.order <= order))
    .sort((a, b) => a.order - b.order)
    .map((slide) => Number(slide.id)),

  canUseNewLeaderboard(_, __, rootState, rootGetters) {
    if (!isFeatureEnabled(rootGetters, UNLEASH_FLAG_NEW_LEADERBOARD)) return false
    const turnOnDate = getVariant(rootGetters, UNLEASH_FLAG_NEW_LEADERBOARD)?.payload?.value
    const { presentation, slides } = rootState
    if (!turnOnDate || !presentation) return false
    // gate by last reset (if available) or createdAt
    const resetTimestamp = Number(presentation.resetTimeStamp)
    const boundary = Number.isFinite(resetTimestamp) && resetTimestamp > 0 ? resetTimestamp : presentation.createdAt
    if (!boundary) return false
    if (new Date(boundary) <= new Date(turnOnDate)) return false
    // gate to decks that actually contain a plugin quiz slide
    return (slides || []).some((slide) => rootGetters['embedApp/isScoredSlide'](slide))
  },

  isLeaderboardVisible(state, getters) {
    if (!getters.canUseNewLeaderboard) return false
    return state.isLeaderboardVisible
  },
}

const actions = {
  // show/hide the leaderboard during the quiz
  subscribeLeaderboardVisibilityAction: async ({ getters, dispatch }, { presentation }) => {
    if (!getters.canUseNewLeaderboard) return
    dispatch('kvSync/subscribeKeyAction', {
      key: `/leaderboards/${presentation.id}`,
    }, { root: true })
  },

  fetchLeaderboardSlideTopNAction: async ({ rootState, getters }, { subject, order, n = 100 } = {}) => {
    const slideIds = getters.quizSlideIdsUpTo(order)
    return LeaderboardApi.getLeaderboardSlideTopN({
      presentationId: rootState.presentation.id,
      presentationVersion: rootState.presentation.version,
      subject,
      teamScoringRule: rootState.presentation.teamScoringRule,
      slideIds,
      lastSlideId: slideIds.length >= 2 ? slideIds[slideIds.length - 2] : undefined,
      n,
    })
  },

  fetchLeaderboardSlideAroundAction: async ({ rootState, getters }, {
    subject, subjectId, order, k = 10,
  } = {}) => {
    const slideIds = getters.quizSlideIdsUpTo(order)
    return LeaderboardApi.getLeaderboardSlideAround({
      presentationId: rootState.presentation.id,
      presentationVersion: rootState.presentation.version,
      subject,
      subjectId: subjectId || rootState.audienceId,
      teamScoringRule: rootState.presentation.teamScoringRule,
      slideIds,
      k,
    })
  },
}

const mutations = {
  [MUTATION_TYPES.TOGGLE_LEADERBOARD_VISIBILITY](state, isVisible) {
    if (typeof isVisible !== 'boolean') {
      state.isLeaderboardVisible = !state.isLeaderboardVisible
      return
    }
    state.isLeaderboardVisible = isVisible
  },
}

const plugins = {
  updateLeaderboardVisibility: (store) => {
    store.subscribeAction((action) => {
      if (action.type === 'kvSync/updateKeyValueAction') {
        const { key, value } = action.payload
        if (key.includes('/leaderboards/')) {
          store.commit(`leaderboard/${MUTATION_TYPES.TOGGLE_LEADERBOARD_VISIBILITY}`, JSON.parse(value).isVisible)
        }
      }
    })
  },
}

const leaderboardModule = {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
  plugins,
}

export default leaderboardModule
